/** Orchestrates enemy AI by delegating to detection, aggro, facing, pathfinding, search, chase, and duty subsystems. */
import { Component } from './Component';
import type { EnemyArchetypeConfig, Position, PlayerHideState } from '../utils/Types';
import { DebugLog } from '../debug/DebugLog';
import { Detection } from '../ai/Detection';
import { Aggro } from '../ai/Aggro';
import { Facing } from '../ai/Facing';
import { PathHelper } from '../ai/PathHelper';
import { SearchSystem } from '../ai/SearchSystem';
import { DutySystem } from '../ai/DutySystem';
import { ChaseSystem } from '../ai/ChaseSystem';
import type { ChaseContext, ChaseStateUpdate } from '../ai/ChaseSystem';
import { SprintSystem, NO_SPRINT } from '../ai/SprintSystem';
import { type AIState, type DutySubstate, type MovementResult, ZERO_MOVEMENT, ALERT_HWM_THRESHOLD, ALERT_AGGRO_FLOOR, ALERT_DURATION_MS, SEARCH_MEMORY_DIST_SQ, RECENTLY_DAMAGED_WINDOW_MS, CHASE_REENTRY_COOLDOWN_MS } from '../ai/constants';
import type { CorpseRecord } from '../systems/EnemyManager';
import type { EnemySkillRegistry } from '../skills/enemy/EnemySkillRegistry';
import { distance, distanceSq, radiansToDegrees, directionToAngle, scaledNormalize } from '../utils/Math';
import {
  type CorpseInvestigationState,
  createCorpseInvestigationState,
  checkNearbyCorpses as corpseCheck,
  resolveCorpseArrival,
  resetCorpseBlacklist,
} from '../ai/behaviors/CorpseInvestigationBehavior';

// Re-export so existing consumers (DebugOverlay, etc.) don't need to change imports
export type { AIState };

/** Thin orchestrator for enemy AI: wires detection, aggro, facing, pathfinding, search, chase, and duty subsystems together each frame. */
export class AIComponent implements Component {
  state: AIState = 'duty';
  private config: EnemyArchetypeConfig;

  // Subsystems
  private detection: Detection;
  private aggroSystem: Aggro;
  private facing: Facing;
  private pathHelper: PathHelper;
  private searchSystem: SearchSystem;
  private dutySystem: DutySystem;
  private chaseSystem: ChaseSystem;
  private sprintSystem: SprintSystem;

  // Chase state
  private lastKnownX: number = 0;
  private lastKnownY: number = 0;
  private hasLastKnown: boolean = false;
  private snapFacingToLKP: boolean = false;

  // Stagger state
  private staggerTimer: number = 0;
  private staggerIsSilent: boolean = false;

  // Alert state
  private alertTimer: number = 0;

  // Chase reentry cooldown — minimum time after leaving chase before re-entering (prevents oscillation)
  private _chaseCooldownTimer: number = 0;

  // Recently damaged flag (set by spikeAggro, decays over 2s)
  private _recentlyDamagedTimer: number = 0;

  // Cached distance (updated each frame)
  private _distToPlayer: number = Infinity;

  // Sprint multiplier for current frame (set by sprint tick)
  private _sprintMultiplier: number = 1.0;

  // One-frame flag: true when enemy transitions from chase/using_skill to search/alert/duty
  private _justLostPlayer: boolean = false;

  // Skill registry (ADR-033) — all archetypes have skills
  private _skillRegistry: EnemySkillRegistry;

  // Optional callback to count nearby allies (wired by GameScene for buff/backup preconditions)
  private _countNearbyAllies: ((x: number, y: number, radius: number) => number) | null = null;

  // Optional callback to get active corpse positions (wired by GameScene for corpse investigation)
  private _getCorpses: (() => readonly CorpseRecord[]) | null = null;

  // Corpse investigation behavior state (blacklist of already-investigated positions)
  private _corpseInvestigation: CorpseInvestigationState = createCorpseInvestigationState();

  // Bound logger — checks aiLogging flag automatically
  private log: (msg: string) => void;

  /** Tag used in all log messages and throttle keys (e.g. "grunt#3"). */
  private _tag: string;

  constructor(config: EnemyArchetypeConfig, dutyX: number, dutyY: number, groundLayer: Phaser.Tilemaps.TilemapLayer, skillRegistry: EnemySkillRegistry, instanceTag?: string) {
    this.config = config;
    this._tag = instanceTag ? `${config.id}:${instanceTag}` : config.id;
    this.log = DebugLog.logger('ai', this._tag);
    this.detection = new Detection(config, groundLayer);
    this.aggroSystem = new Aggro(config);
    this.facing = new Facing(groundLayer);
    const pathSeed = instanceTag ? parseInt(instanceTag, 16) || 0 : 0;
    this.pathHelper = new PathHelper(groundLayer, pathSeed, this._tag);
    this.searchSystem = new SearchSystem(groundLayer, this.pathHelper);
    this.dutySystem = new DutySystem(config, dutyX, dutyY, groundLayer, this.pathHelper);
    this.chaseSystem = new ChaseSystem(config, this.pathHelper, this.detection, this.aggroSystem, this.searchSystem, skillRegistry);
    this.sprintSystem = new SprintSystem(config.sprint ?? NO_SPRINT);
    for (const msg of this.dutySystem.drainInitLogs()) this.log(msg);
    this._skillRegistry = skillRegistry;
  }

  /** Assign a stable instance tag (e.g. from EnemyManager). Rebinds the logger to "archetype:tag". */
  setInstanceTag(tag: string): void {
    this._tag = `${this.config.id}:${tag}`;
    this.log = DebugLog.logger('ai', this._tag);
  }

  /** The tag used in log messages (e.g. "grunt#3"). */
  get tag(): string { return this._tag; }

  // --- Public getters (used by DebugOverlay, CombatSystem, Enemy) ---

  get aggro(): number { return this.aggroSystem.aggro; }
  get hasLOS(): boolean { return this.detection.hasLOS; }
  get facingX(): number { return this.facing.facingX; }
  get facingY(): number { return this.facing.facingY; }
  get aggroHWM(): number { return this.aggroSystem.aggroHWM; }
  get distToPlayer(): number { return this._distToPlayer; }
  get detectionRadius(): number { return this.detection.effectiveDetectionRadius; }
  get visionHalfAngleRad(): number { return this.detection.visionHalfAngleRad; }
  get soundConeHalfAngleRad(): number { return this.detection.soundConeHalfAngleRad; }
  get soundConeRangeVal(): number { return this.detection.soundConeRangeVal; }
  get soundConeCenterRad(): number { return this.detection.soundConeCenterRad; }
  get detectedBySound(): boolean { return this.detection.detectedBySound; }

  get currentPath(): readonly Position[] { return this.pathHelper.currentPath; }
  get currentPathIndex(): number { return this.pathHelper.currentPathIndex; }
  get searchMemory(): readonly Position[] { return this.searchSystem.currentSearchMemory; }
  get dutySubstate(): DutySubstate { return this.dutySystem.substate; }
  get sprint(): SprintSystem { return this.sprintSystem; }
  get activeSkill() { return this._skillRegistry.activeSkill; }
  get skillRegistry(): EnemySkillRegistry { return this._skillRegistry; }

  /** Current patrol waypoints (for Trace visualization). Empty if not patrolling. */
  get patrolWaypoints(): ReadonlyArray<{ x: number; y: number }> { return this.dutySystem.getPatrolWaypoints(); }

  /** Current waypoint index in patrol circuit. */
  get patrolWaypointIndex(): number { return this.dutySystem.currentWaypointIndex; }

  /** External aggro decay multiplier — set by Marked passive (1.0 = normal, >1.0 = slower decay). */
  get aggroDecayMultiplier(): number { return this.aggroSystem.decayMultiplier; }
  set aggroDecayMultiplier(v: number) { this.aggroSystem.decayMultiplier = v; }

  /** True for one frame after enemy transitions from chase/using_skill to search/alert/duty (lost the player). */
  get justLostPlayer(): boolean { return this._justLostPlayer; }

  /** The enemy's assigned duty post coordinates. */
  get dutyPosition(): { x: number; y: number } {
    return { x: this.dutySystem.dutyX, y: this.dutySystem.dutyY };
  }

  /** Reassign the enemy's duty post. Used when spawning at a perimeter point with an intended post elsewhere. */
  setDutyPosition(x: number, y: number): void {
    for (const msg of this.dutySystem.setDutyPosition(x, y)) this.log(msg);
  }

  /** Set facing to a unit vector (used for spawn randomization). */
  setFacing(fx: number, fy: number): void {
    this.facing.setFacing(fx, fy);
  }

  /** Set the callback used to count nearby allies for buff/backup skill preconditions. */
  setAllyCounter(counter: (x: number, y: number, radius: number) => number): void {
    this._countNearbyAllies = counter;
  }

  /** Set the callback to get active corpse positions for investigation. */
  setCorpseProvider(provider: () => readonly CorpseRecord[]): void {
    this._getCorpses = provider;
  }

  /** Check whether this enemy can perceive an agitated ally at the given position (via vision or sound cones). */
  canPerceiveAlertedEnemy(entityX: number, entityY: number, targetX: number, targetY: number): boolean {
    return this.detection.canPerceiveAlertedAlly(
      entityX, entityY, targetX, targetY,
      this.facing.facingX, this.facing.facingY, this.state,
    );
  }

  /** Immediate transition to alert from perceiving an agitated ally. Only affects duty-state enemies.
   *  Receiver's aggro is capped at the source's level — propagation never amplifies. */
  alertFromAlly(sourceAggro: number): void {
    if (this.state !== 'duty') return;
    if (this.aggroSystem.aggro < sourceAggro) {
      this.aggroSystem.setTo(sourceAggro);
    }
    this.state = 'alert';
    this.alertTimer = ALERT_DURATION_MS;
    this.log(`ALERT from ally (src=${Math.round(sourceAggro)}) → aggro=${Math.round(this.aggroSystem.aggro)}`);
  }

  /** Bump aggro by a fixed amount (combat sound alert). Transitions to alert if threshold crossed; no-op if already chasing. */
  raiseAlert(amount: number): void {
    if (this.state === 'chase' || this.state === 'using_skill') return;
    const prev = this.aggroSystem.aggro;
    this.aggroSystem.raise(amount);
    DebugLog.throttled(`ai-alert-${this._tag}`, 2000, 'ai',
      `[${this._tag}] ALERT bump +${amount} aggro=${Math.round(prev)}→${Math.round(this.aggroSystem.aggro)} state=${this.state}`);
    if (this.aggroSystem.aggro >= ALERT_HWM_THRESHOLD && (this.state === 'duty' || this.state === 'search')) {
      this.state = 'alert';
      this.alertTimer = ALERT_DURATION_MS;
      this.log(`ALERT threshold crossed aggro=${Math.round(this.aggroSystem.aggro)} → alert (combat sound)`);
    }
  }

  /**
   * Delegate nearby-corpse detection to CorpseInvestigationBehavior.
   * Only runs in duty state for archetypes with panicOnLeaderDeath, and only
   * when the entity is not already in the investigate substate.
   */
  private runCorpseCheck(entityX: number, entityY: number): void {
    if (!this._getCorpses || !this.config.panicOnLeaderDeath) return;
    if (this.state !== 'duty') return;
    if (this.dutySystem.substate === 'investigate') return;

    const logs: string[] = [];
    const result = corpseCheck(
      this._corpseInvestigation,
      entityX, entityY,
      this._getCorpses(),
      this.detection,
      logs,
    );

    // Relay throttled blacklist hits through DebugLog so they don't flood the channel
    for (const msg of logs) {
      if (msg.startsWith('CORPSE skip blacklisted')) {
        DebugLog.throttled(`ai-blacklist-${this._tag}`, 5000, 'ai', `[${this._tag}] ${msg}`);
      } else {
        this.log(msg);
      }
    }

    if (result.action === 'spike') {
      this.spikeAggro(result.x, result.y);
    } else if (result.action === 'investigate') {
      const stopDist = this.dutySystem.setInvestigateTarget(result.x, result.y);
      this.log(`CORPSE sensed at (${Math.round(result.x)},${Math.round(result.y)}) → investigate (stopDist=${Math.round(stopDist)})`);
    }
  }

  /** Choose the best state to resume after an interrupt (stagger end, skill complete) based on current aggro and LKP.
   *  Uses the same distance-scaled chase threshold as updateStateTransitions to avoid post-stagger oscillation. */
  private pickPostInterruptState(distToPlayer: number): AIState {
    const effectiveChaseThreshold = this.computeEffectiveChaseThreshold(distToPlayer);
    if (this.aggroSystem.aggro >= effectiveChaseThreshold) return 'chase';
    if (this.aggroSystem.aggro > this.config.disengageThreshold && this.hasLastKnown) return 'search';
    if (this.aggroSystem.aggroHWM >= ALERT_HWM_THRESHOLD) return 'alert';
    return 'duty';
  }

  /** Instantly max out aggro and optionally seed the last-known position, forcing chase state. */
  spikeAggro(attackerX?: number, attackerY?: number): void {
    this.aggroSystem.spike();
    this._recentlyDamagedTimer = RECENTLY_DAMAGED_WINDOW_MS;
    this._chaseCooldownTimer = 0; // damage overrides cooldown
    if (attackerX !== undefined && attackerY !== undefined) {
      this.lastKnownX = attackerX;
      this.lastKnownY = attackerY;
      this.hasLastKnown = true;
      this.snapFacingToLKP = true;
    }

    if (this.state === 'duty' || this.state === 'search' || this.state === 'alert') {
      this.state = 'chase';
      this.pathHelper.clearPath();
      this.searchSystem.resetForChase();
      this.dutySystem.resetReturn();
      this.alertTimer = 0;
    }
    this.log(`SPIKE aggro=100 hold=${this.config.aggroHoldDuration}s → chase LKP=(${Math.round(this.lastKnownX)},${Math.round(this.lastKnownY)})`);
  }

  /**
   * Directly inject AI state for simulation/testing only — bypasses the normal state machine.
   * Call via SimulationBridge immediately after spawn, before the first tick.
   */
  forceSimState(options: {
    mode?: 'duty' | 'alert' | 'chase' | 'search';
    aggro?: number;
    lkp?: { x: number; y: number };
  }): void {
    if (options.aggro !== undefined) {
      if (options.aggro >= 100) {
        this.aggroSystem.spike();
      } else {
        this.aggroSystem.setTo(options.aggro);
      }
    }
    if (options.lkp !== undefined) {
      this.lastKnownX = options.lkp.x;
      this.lastKnownY = options.lkp.y;
      this.hasLastKnown = true;
      this.snapFacingToLKP = true;
    }
    if (options.mode !== undefined) {
      const prev = this.state;
      this.state = options.mode;
      if (options.mode !== 'duty') {
        this.pathHelper.clearPath();
        this.dutySystem.resetReturn();
        this.alertTimer = 0;
      }
      if (options.mode === 'chase') {
        this.searchSystem.resetForChase();
      }
      this.log(`SIM state injected: ${prev} → ${options.mode} aggro=${options.aggro ?? '–'} lkp=(${Math.round(options.lkp?.x ?? 0)},${Math.round(options.lkp?.y ?? 0)})`);
    }
  }

  /** Wipe aggro — enemy forgets the player. State machine handles transition on next tick. */
  wipeAggro(): void {
    this.aggroSystem.reset();
    this.hasLastKnown = false;
    this._skillRegistry.interrupt();
    this.pathHelper.clearPath();
    this.log('AGGRO WIPED (stealth activation)');
  }

  /** Hard-reset all AI state back to duty — used when the player dies. */
  resetToDuty(entityX: number, entityY: number): void {
    this.aggroSystem.reset();
    this.state = 'duty';
    this.hasLastKnown = false;
    this.alertTimer = 0;
    this.staggerTimer = 0;
    this._recentlyDamagedTimer = 0;
    this._chaseCooldownTimer = 0;
    resetCorpseBlacklist(this._corpseInvestigation);
    this.pathHelper.clearPath();
    this.searchSystem.clearMemory();
    this._skillRegistry.interrupt();
    this.sprintSystem.reset();
    this.dutySystem.resetReturn();
    for (const msg of this.dutySystem.resetToPatrol(entityX, entityY)) this.log(msg);
    this.log('RESET to duty (player death)');
  }

  /** Interrupt the enemy into a frozen stagger state for the given duration.
   *  Pass silent=true for mechanical staggers (knockback, death stun).
   *  Immunity is handled externally by StatusEffectTracker — callers must
   *  check tracker.isImmune('stagger') before calling for non-silent staggers.
   *  Returns true unconditionally (kept for API compatibility). */
  applyStagger(durationMs: number, silent = false): boolean {
    this._skillRegistry.interrupt();
    this.state = 'stagger';
    this.staggerTimer = durationMs;
    this.staggerIsSilent = silent;
    return true;
  }

  /** Run the full AI tick and return the desired velocity and attack intent for this frame. */
  getMovement(entityX: number, entityY: number, playerX: number, playerY: number, delta: number, playerHideState?: PlayerHideState): MovementResult {
    // Clear one-frame Marked trigger flag (set by updateStateTransitions)
    this._justLostPlayer = false;

    // --- Distance ---
    const dx = playerX - entityX;
    const dy = playerY - entityY;
    const distToPlayer = distance(entityX, entityY, playerX, playerY);
    this._distToPlayer = distToPlayer;

    // --- Deferred facing snap from spikeAggro ---
    if (this.snapFacingToLKP) {
      this.snapFacingToLKP = false;
      this.facing.snapToward(this.lastKnownX, this.lastKnownY, entityX, entityY);
    }

    // --- Detection ---
    const { losChanged } = this.detection.update(
      entityX, entityY, playerX, playerY,
      this.facing.facingX, this.facing.facingY, this.state,
      playerHideState,
    );
    if (losChanged) {
      const sense = this.detection.detectedBySound ? 'SOUND' : 'VISION';
      this.log(`LOS ${this.detection.hasLOS ? `ACQUIRED(${sense})` : 'LOST'} dist=${Math.round(distToPlayer)} pos=(${Math.round(entityX)},${Math.round(entityY)})`);

      // Re-acquiring player during chase invalidates stale exit predictions —
      // but only if the player is seen far from where we predicted exits.
      // Brief LOS flickers near the same area must NOT wipe the chain.
      if (this.detection.hasLOS && this.state === 'chase' && this.searchSystem.hasPredicted) {
        const predDistSq = distanceSq(playerX, playerY, this.searchSystem.predictedFromX, this.searchSystem.predictedFromY);
        if (predDistSq > SEARCH_MEMORY_DIST_SQ) {
          this.searchSystem.resetForChase();
          this.log(`SEARCH MEMORY cleared (chase reacquisition, player far from prediction origin)`);
        }
      }
    }

    // --- Track last known position ---
    if (this.detection.hasLOS) {
      this.lastKnownX = playerX;
      this.lastKnownY = playerY;
      this.hasLastKnown = true;
    }

    // --- Aggro ---
    const aggroLog = this.aggroSystem.update(
      this.detection.hasLOS, distToPlayer, this.detection.effectiveDetectionRadius,
      this.state, delta,
    );
    if (aggroLog) this.log(aggroLog);

    // Reset HWM and search memory on full decay
    if (this.aggroSystem.aggro <= 0) {
      this.searchSystem.clearMemory();
    }

    // --- Corpse investigation (duty state only) ---
    this.runCorpseCheck(entityX, entityY);

    // Handle investigate arrival: check LOS to corpse → spike or fall back to patrol
    if (this.state === 'duty' && this.dutySystem.substate === 'investigate' && this.dutySystem.investigateArrived) {
      const arrivalLogs: string[] = [];
      const arrival = resolveCorpseArrival(
        this._corpseInvestigation,
        entityX, entityY,
        this.dutySystem.investigateTargetX,
        this.dutySystem.investigateTargetY,
        this.detection,
        arrivalLogs,
      );
      for (const msg of arrivalLogs) this.log(msg);

      if (arrival.action === 'spike') {
        this.spikeAggro(arrival.x, arrival.y);
      } else {
        for (const msg of this.dutySystem.resetToPatrol(entityX, entityY)) this.log(msg);
      }
    }

    // --- Status log (throttled) ---
    const investigateInfo = this.dutySystem.substate === 'investigate'
      ? ` invTarget=(${Math.round(this.dutySystem.investigateTargetX)},${Math.round(this.dutySystem.investigateTargetY)}) invDist=${Math.round(distance(entityX, entityY, this.dutySystem.investigateTargetX, this.dutySystem.investigateTargetY))}`
      : '';
    const dutyInfo = this.state === 'duty' || this.state === 'alert'
      ? ` duty:${this.dutySystem.substate} distPost=${Math.round(distance(entityX, entityY, this.dutySystem.dutyX, this.dutySystem.dutyY))} wpts=${this.dutySystem.waypointCount}${investigateInfo}`
      : '';
    DebugLog.throttled(`ai-status-${this._tag}`, 500, 'ai',
      `[${this._tag}] status: state=${this.state}${dutyInfo} pos=(${Math.round(entityX)},${Math.round(entityY)}) aggro=${Math.round(this.aggroSystem.aggro)} hwm=${Math.round(this.aggroSystem.aggroHWM)} LOS=${this.detection.hasLOS} hold=${Math.round(this.aggroSystem.holdTimer)}ms dist=${Math.round(distToPlayer)} LKP=${this.hasLastKnown ? `(${Math.round(this.lastKnownX)},${Math.round(this.lastKnownY)})` : 'none'} playerDir=(${this.detection.playerDirX.toFixed(2)},${this.detection.playerDirY.toFixed(2)})`);

    // --- Skill registry tick (cooldowns for all skills, phase timing for active) ---
    this._skillRegistry.update(delta);

    // --- Recently-damaged decay ---
    if (this._recentlyDamagedTimer > 0) {
      this._recentlyDamagedTimer -= delta;
    }

    // --- Sprint tick (chase + LOS = wants sprint) ---
    const wantsSprint = this.state === 'chase' && this.detection.hasLOS;
    this._sprintMultiplier = this.sprintSystem.update(delta, wantsSprint);

    // --- Stagger override ---
    if (this.state === 'stagger') {
      this.staggerTimer -= delta;
      if (this.staggerTimer <= 0) {
        const nextState = this.pickPostInterruptState(distToPlayer);
        this.log(`stagger ended → ${nextState} (aggro=${Math.round(this.aggroSystem.aggro)})`);
        this.state = nextState;
        if (nextState === 'search') {
          this.enterSearch(entityX, entityY);
        } else if (nextState === 'alert') {
          this.alertTimer = ALERT_DURATION_MS;
        }
      }
      return ZERO_MOVEMENT;
    }

    // --- Skill-based attack (ADR-033) ---
    if (this.state === 'using_skill') {
      const skill = this._skillRegistry.activeSkill;
      if (!skill || skill.isComplete()) {
        const nextState = this.pickPostInterruptState(distToPlayer);
        this.log(`skill complete → ${nextState}`);
        // Detect "lost player" transition from using_skill
        if (nextState === 'search' || nextState === 'alert' || nextState === 'duty') {
          this._justLostPlayer = true;
        }
        this.state = nextState;
        if (nextState === 'search') {
          this.enterSearch(entityX, entityY);
        } else if (nextState === 'alert') {
          this.alertTimer = ALERT_DURATION_MS;
        }
      } else {
        const movement = skill.getMovement();
        return movement
          ? { vx: movement.vx, vy: movement.vy, wantsAttack: false }
          : ZERO_MOVEMENT;
      }
    }

    // --- State transitions ---
    this.updateStateTransitions(distToPlayer, entityX, entityY, delta);

    // --- Duty/alert advance toward player ---
    if ((this.state === 'duty' || this.state === 'alert') && this.detection.hasLOS) {
      const distFromDutySq = distanceSq(entityX, entityY, this.dutySystem.dutyX, this.dutySystem.dutyY);
      if (distFromDutySq < this.detection.effectiveDetectionRadiusSq) {
        const vel = scaledNormalize(dx, dy, this.config.moveSpeed);
        return { vx: vel.x, vy: vel.y, wantsAttack: false };
      }
    }

    // --- Movement dispatch ---
    if (this.state === 'search') {
      const { movement, logs } = this.searchSystem.searchMovement(entityX, entityY, this.aggroSystem.aggroHWM, this.config.moveSpeed);
      for (const msg of logs) this.log(msg);
      return movement;
    }

    if (this.state === 'alert') {
      const { movement, logs } = this.dutySystem.movement(entityX, entityY, delta);
      for (const msg of logs) this.log(msg);
      return movement;
    }

    if (this.state === 'chase') {
      const ctx: ChaseContext = {
        entityX, entityY, dx, dy, distToPlayer, delta,
        hasLOS: this.detection.hasLOS,
        hasLastKnown: this.hasLastKnown,
        lastKnownX: this.lastKnownX,
        lastKnownY: this.lastKnownY,
        recentlyDamaged: this._recentlyDamagedTimer > 0,
        sprintMultiplier: this._sprintMultiplier,
        facingX: this.facing.facingX,
        facingY: this.facing.facingY,
        playerDirX: this.detection.playerDirX,
        playerDirY: this.detection.playerDirY,
        countNearbyAllies: this._countNearbyAllies,
      };
      const { movement, stateUpdate, logs } = this.chaseSystem.getMovement(ctx);
      for (const msg of logs) this.log(msg);
      this.applyChaseStateUpdate(stateUpdate);
      return movement;
    }

    // Duty
    const { movement, logs } = this.dutySystem.movement(entityX, entityY, delta);
    for (const msg of logs) this.log(msg);
    return movement;
  }

  /** Update the facing direction based on current movement, state, and head-swivel logic. */
  updateFacing(vx: number, vy: number, delta: number, entityX: number, entityY: number): void {
    this.facing.update(vx, vy, delta, entityX, entityY, this.state, this.aggroSystem.aggro, this.dutySystem.substate);

    const moving = vx * vx + vy * vy > 0.01;
    const sweeping = this.state === 'search' || this.state === 'alert' || (this.state === 'duty' && this.aggroSystem.aggro > 0);
    const facingDeg = Math.round(radiansToDegrees(directionToAngle(this.facing.facingX, this.facing.facingY)));
    DebugLog.throttled(`ai-facing-${this._tag}`, 1000, 'ai',
      `[${this._tag}] FACING state=${this.state} moving=${moving} sweep=${sweeping} angle=${facingDeg}°`);
  }

  // --- State transition logic ---

  /** Distance-scaled chase threshold — ensures post-interrupt and transition logic use identical thresholds. */
  private computeEffectiveChaseThreshold(distToPlayer: number): number {
    const distRatio = Math.min(distToPlayer / this.detection.effectiveDetectionRadius, 1);
    return Math.max(this.config.chaseThreshold * distRatio, this.config.disengageThreshold + 1);
  }

  /** Evaluate aggro thresholds and LOS to drive duty↔alert↔chase↔search transitions each frame. Sets _justLostPlayer on chase→search/alert/duty edges. */
  private updateStateTransitions(distToPlayer: number, entityX: number, entityY: number, delta: number): void {
    const effectiveChaseThreshold = this.computeEffectiveChaseThreshold(distToPlayer);
    const prevState = this.state;

    // Tick down chase reentry cooldown
    if (this._chaseCooldownTimer > 0) this._chaseCooldownTimer -= delta;

    const canEnterChase = this._chaseCooldownTimer <= 0;

    if ((this.state === 'duty' || this.state === 'alert') && canEnterChase && this.aggroSystem.aggro >= effectiveChaseThreshold && (this.detection.hasLOS || this.hasLastKnown)) {
      this.state = 'chase';
      this.pathHelper.clearPath();
      this.searchSystem.resetForChase();
      this.log(`SEARCH MEMORY cleared (duty/alert→chase)`);
      this.dutySystem.resetReturn();
      this.alertTimer = 0;
    } else if ((this.state === 'chase' || this.state === 'search') && this.aggroSystem.aggro <= this.config.disengageThreshold) {
      this._chaseCooldownTimer = CHASE_REENTRY_COOLDOWN_MS;
      if (this.aggroSystem.aggroHWM >= ALERT_HWM_THRESHOLD) {
        this.state = 'alert';
        this.alertTimer = ALERT_DURATION_MS;
        this.pathHelper.clearPath();
      } else {
        this.state = 'duty';
        this.hasLastKnown = false;
        this.pathHelper.clearPath();
        for (const msg of this.dutySystem.resetToPatrol(entityX, entityY)) this.log(msg);
      }
    } else if (this.state === 'search' && this.detection.hasLOS && canEnterChase) {
      this.state = 'chase';
      this.searchSystem.resetForChase();
      this.log(`SEARCH MEMORY cleared (search→chase reacquisition)`);
      this.pathHelper.clearPath();
    } else if (this.state === 'alert') {
      this.alertTimer -= delta;
      if (this.alertTimer <= 0) {
        this.state = 'duty';
        this.hasLastKnown = false;
        this.alertTimer = 0;
        for (const msg of this.dutySystem.resetToPatrol(entityX, entityY)) this.log(msg);
      }
    }

    if (this.state !== prevState) {
      this.log(`STATE ${prevState} → ${this.state} (aggro=${Math.round(this.aggroSystem.aggro)} threshold=${Math.round(effectiveChaseThreshold)} LOS=${this.detection.hasLOS})`);

      // Detect "lost player" transitions for Marked passive
      const wasChasing = prevState === 'chase' || prevState === 'using_skill';
      const nowSearching = this.state === 'search' || this.state === 'alert' || this.state === 'duty';
      if (wasChasing && nowSearching) {
        this._justLostPlayer = true;
      }
    }
  }

  // --- Chase state application ---

  /** Apply state mutations returned by ChaseSystem to AIComponent's owned state. */
  private applyChaseStateUpdate(update: ChaseStateUpdate): void {
    if (update.clearPath) this.pathHelper.clearPath();
    if (update.lastKnownX !== undefined) this.lastKnownX = update.lastKnownX;
    if (update.lastKnownY !== undefined) this.lastKnownY = update.lastKnownY;
    if (update.hasLastKnown !== undefined) this.hasLastKnown = update.hasLastKnown;
    if (update.newState !== undefined) {
      if (update.newState === 'search') this._chaseCooldownTimer = CHASE_REENTRY_COOLDOWN_MS;
      this.state = update.newState;
    }
  }

  /** Transition to search state and initialize the search pattern from the current position and last-known player direction. */
  private enterSearch(entityX: number, entityY: number): void {
    this.state = 'search';
    this._chaseCooldownTimer = CHASE_REENTRY_COOLDOWN_MS;
    const logs = this.searchSystem.beginSearch(entityX, entityY, this.detection.playerDirX, this.detection.playerDirY);
    for (const msg of logs) this.log(msg);
  }

  /** No-op tick — all AI state updates happen in getMovement. */
  update(_delta: number): void {
    // State updates handled in getMovement
  }

  /** Clean up skill registry. */
  destroy(): void {
    this._skillRegistry.destroy();
  }
}
